import {
  InvestorIndividualInfo, InvestorOrganizationInfo, ProjectInvestorInfo, ProjectStakeHolderInfo, ProjectTeamInfo,
  StakeHolderIndividualInfo,
  StakeHolderOrganizationInfo,
  TeamIndividualInfo, TeamOrganizationInfo
} from '~/api/repository'
import { IMAGE } from '~/constants/image.constants'
import { formatAbbrDate } from '~/utils/date'
import { capitalizeFirstLetter, capitalizeFirstLetterWithDelimiter } from '~/utils/string'

class IndividualModel {
  private readonly _logo: string
  private readonly _name: string
  private readonly _title: string
  private readonly _type?: string

  constructor (logo: string, name: string, title: string, type?: string) {
    this._logo = logo
    this._name = name
    this._title = title
    this._type = type
  }

  get logo () {
    return this._logo || IMAGE.thumbnail.person
  }

  get name () {
    return this._name
  }

  get title () {
    return this._title
  }

  get type () {
    if (!this._type) {
      return ''
    }
    return capitalizeFirstLetterWithDelimiter(this._type, ' ')
  }
}

class StakeholderIndividualModel extends IndividualModel {
  private readonly _stakeholderType: string
  private readonly _contributorType: string

  constructor (res: StakeHolderIndividualInfo) {
    super(res.LG, res.NM, res.TI, res.STHDRTP)
    this._stakeholderType = res.STHDRTP
    this._contributorType = res.CTBRTP
  }

  get stakeholderType () {
    return this._stakeholderType
  }

  get contributorType () {
    return this._contributorType
  }
}

class TeamIndividualModel extends IndividualModel {
  private readonly _relatedTypeList: Array<string>

  constructor (res: TeamIndividualInfo) {
    super(res.LG, res.NM, res.TI, res.RTPLIST[0])
    this._relatedTypeList = res.RTPLIST
  }

  get relatedTypeList () {
    return this._relatedTypeList
  }
}

class InvestorIndividualModel extends IndividualModel {
  constructor (res: InvestorIndividualInfo) {
    super(res.LG, res.NM, res.TI)
  }
}

export class OrganizationModel {
  private readonly _logo: string
  private readonly _name: string
  private readonly _description: string
  private readonly _type: string
  private readonly _websiteUrl: string
  private readonly _establishDate: string
  private readonly _address: string
  private readonly _entityRegistrationNumber: string
  private readonly _jurisdictionOfIncorporation: string

  constructor (res: StakeHolderOrganizationInfo | TeamOrganizationInfo | InvestorOrganizationInfo) {
    this._logo = res.LG
    this._name = res.NM
    this._description = res.DESC
    this._type = res.TP
    this._websiteUrl = res.WSURL
    this._establishDate = res.ESTDT
    this._address = res.ADDR
    this._entityRegistrationNumber = res.EREGNUM
    this._jurisdictionOfIncorporation = res.JINC
  }

  get logo () {
    return this._logo || IMAGE.thumbnail.person
  }

  get name () {
    return this._name
  }

  get description () {
    return this._description
  }

  get type () {
    if (!this._type) {
      return '-'
    }
    return capitalizeFirstLetter(this._type)
  }

  get websiteUrl () {
    return this._websiteUrl
  }

  get establishDate () {
    return formatAbbrDate(this._establishDate)
  }

  get address () {
    if (!this._address) {
      return '-'
    }
    return this._address
  }

  get entityRegistrationNumber () {
    if (!this._entityRegistrationNumber) {
      return '-'
    }
    return this._entityRegistrationNumber
  }

  get jurisdictionOfIncorporation () {
    if (!this._jurisdictionOfIncorporation) {
      return '-'
    }
    return this._jurisdictionOfIncorporation
  }
}

export class StakeholderOrganizationModel extends OrganizationModel {
  constructor (res: StakeHolderOrganizationInfo) {
    super(res)
  }
}

export class TeamOrganizationModel extends OrganizationModel {
  private readonly _relatedTypeList: Array<string>

  constructor (res: TeamOrganizationInfo) {
    super(res)
    this._relatedTypeList = res.RTPLIST
  }

  get relatedTypeList () {
    return this._relatedTypeList
  }
}

export class InvestorOrganizationModel extends OrganizationModel {
  constructor (res: InvestorOrganizationInfo) {
    super(res)
  }
}

export class StakeholderModel {
  private readonly _people: Array<StakeholderIndividualModel>
  private readonly _organization: Array<StakeholderOrganizationModel>

  constructor (res: ProjectStakeHolderInfo) {
    this._people = res.PPL.map(person => new StakeholderIndividualModel(person))
    this._organization = res.ORG.map(org => new StakeholderOrganizationModel(org))
  }

  get people () {
    return this._people
  }

  get organization () {
    return this._organization
  }

  get totalLength () {
    return this._people.length + this._organization.length
  }
}

export class TeamModel {
  private readonly _people: Array<TeamIndividualModel>
  private readonly _organization: Array<TeamOrganizationModel>

  constructor (res: ProjectTeamInfo) {
    this._people = res.PPL.map(person => new TeamIndividualModel(person))
    this._organization = res.ORG.map(org => new TeamOrganizationModel(org))
  }

  get people () {
    return this._people
  }

  get organization () {
    return this._organization
  }

  get totalLength () {
    return this._people.length + this._organization.length
  }
}

export class InvestorModel {
  private readonly _people: Array<InvestorIndividualModel>
  private readonly _organization: Array<InvestorOrganizationModel>

  constructor (res: ProjectInvestorInfo) {
    this._people = res.PPL.map(person => new InvestorIndividualModel(person))
    this._organization = res.ORG.map(org => new InvestorOrganizationModel(org))
  }

  get people () {
    return this._people
  }

  get organization () {
    return this._organization
  }

  get totalLength () {
    return this._people.length + this._organization.length
  }
}
